import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import {
  MessageSquare, Send, Bot, User, Sparkles, Settings, Plus,
  Trash2, History, ChevronRight, Loader2, X, Minimize2, Maximize2,
  HelpCircle, ClipboardCheck, Calendar, AlertTriangle, FileText
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Provider configurations
const PROVIDERS = {
  openai: { name: 'OpenAI', color: 'green', icon: '🟢' },
  anthropic: { name: 'Anthropic', color: 'orange', icon: '🟠' },
  gemini: { name: 'Google', color: 'blue', icon: '🔵' }
};

// Quick action suggestions
const QUICK_ACTIONS = [
  { icon: ClipboardCheck, text: "Create a safety audit", action: "create_audit" },
  { icon: Calendar, text: "Schedule a meeting", action: "schedule_meeting" },
  { icon: AlertTriangle, text: "Report an observation", action: "create_observation" },
  { icon: FileText, text: "Generate a report", action: "generate_report" }
];

const SUGGESTIONS = [
  "What are the OSHA requirements for fall protection?",
  "Help me prepare for a safety audit",
  "What PPE is required for welding?",
  "Explain lockout/tagout procedures",
  "Create a confined space safety checklist"
];

// Chat Message Component
const ChatMessage = ({ message, isUser }) => {
  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
        isUser ? 'bg-blue-600' : 'bg-gradient-to-br from-purple-500 to-indigo-600'
      }`}>
        {isUser ? (
          <User className="w-4 h-4 text-white" />
        ) : (
          <Bot className="w-4 h-4 text-white" />
        )}
      </div>
      <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
        isUser 
          ? 'bg-blue-600 text-white rounded-tr-sm' 
          : 'bg-gray-100 text-gray-800 rounded-tl-sm'
      }`}>
        <div className="text-sm whitespace-pre-wrap">{message.content}</div>
        {message.action_performed && (
          <div className={`mt-2 pt-2 border-t ${isUser ? 'border-blue-500' : 'border-gray-200'}`}>
            <Badge className={message.action_performed.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
              {message.action_performed.success ? '✓ Action completed' : '✗ Action failed'}
            </Badge>
          </div>
        )}
        <div className={`text-xs mt-1 ${isUser ? 'text-blue-200' : 'text-gray-400'}`}>
          {new Date(message.timestamp).toLocaleTimeString()}
        </div>
      </div>
    </div>
  );
};

// Main AI Assistant Component
const AIAssistant = ({ isFloating = false, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [availableModels, setAvailableModels] = useState({});
  const [selectedProvider, setSelectedProvider] = useState('openai');
  const [selectedModel, setSelectedModel] = useState('gpt-4o');
  const [showSettings, setShowSettings] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const messagesEndRef = useRef(null);
  const { toast } = useToast();

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return { Authorization: `Bearer ${token}` };
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchModels = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/ai-assistant/models`);
      setAvailableModels(response.data.models);
      setSelectedProvider(response.data.default_provider);
      setSelectedModel(response.data.default_model);
    } catch (error) {
      console.error('Error fetching models:', error);
    }
  }, []);

  const fetchSessions = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API_URL}/api/ai-assistant/sessions`, { headers });
      setSessions(response.data.sessions || []);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  }, []);

  useEffect(() => {
    fetchModels();
    fetchSessions();
  }, [fetchModels, fetchSessions]);

  const loadSession = async (sessionId) => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API_URL}/api/ai-assistant/sessions/${sessionId}`, { headers });
      setMessages(response.data.messages || []);
      setCurrentSessionId(sessionId);
      setShowHistory(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load chat session",
        variant: "destructive"
      });
    }
  };

  const startNewSession = () => {
    setMessages([]);
    setCurrentSessionId(null);
    setShowHistory(false);
  };

  const deleteSession = async (sessionId) => {
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API_URL}/api/ai-assistant/sessions/${sessionId}`, { headers });
      fetchSessions();
      if (currentSessionId === sessionId) {
        startNewSession();
      }
      toast({
        title: "Success",
        description: "Chat session deleted"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete session",
        variant: "destructive"
      });
    }
  };

  const sendMessage = async (messageText = input) => {
    if (!messageText.trim()) return;

    const userMessage = {
      role: 'user',
      content: messageText,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const headers = getAuthHeaders();
      const response = await axios.post(`${API_URL}/api/ai-assistant/chat`, {
        message: messageText,
        session_id: currentSessionId,
        provider: selectedProvider,
        model: selectedModel
      }, { headers });

      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: response.data.timestamp,
        action_performed: response.data.action
      };

      setMessages(prev => [...prev, assistantMessage]);
      setCurrentSessionId(response.data.session_id);
      fetchSessions();
    } catch (error) {
      console.error('Chat error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to send message",
        variant: "destructive"
      });
      // Remove the user message if failed
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleProviderChange = (provider) => {
    setSelectedProvider(provider);
    const models = availableModels[provider];
    if (models && models.length > 0) {
      setSelectedModel(models[0].id);
    }
  };

  const containerClass = isFloating 
    ? "fixed bottom-4 right-4 w-96 h-[600px] shadow-2xl rounded-2xl bg-white z-50 flex flex-col"
    : "h-[calc(100vh-200px)] bg-white rounded-2xl shadow-sm flex flex-col";

  return (
    <div className={containerClass}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-t-2xl">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
            <Bot className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold">AI Safety Assistant</h3>
            <p className="text-xs text-white/80">
              {PROVIDERS[selectedProvider]?.icon} {selectedModel}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setShowHistory(!showHistory)}
            className="text-white hover:bg-white/20"
          >
            <History className="w-4 h-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setShowSettings(!showSettings)}
            className="text-white hover:bg-white/20"
          >
            <Settings className="w-4 h-4" />
          </Button>
          {isFloating && onClose && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onClose}
              className="text-white hover:bg-white/20"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div className="p-4 border-b bg-gray-50 space-y-3">
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="text-xs font-medium text-gray-600">Provider</label>
              <Select value={selectedProvider} onValueChange={handleProviderChange}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(PROVIDERS).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      {config.icon} {config.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <label className="text-xs font-medium text-gray-600">Model</label>
              <Select value={selectedModel} onValueChange={setSelectedModel}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableModels[selectedProvider]?.map((model) => (
                    <SelectItem key={model.id} value={model.id}>
                      {model.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}

      {/* History Panel */}
      {showHistory && (
        <div className="absolute top-16 left-0 right-0 bottom-0 bg-white z-10 flex flex-col rounded-b-2xl">
          <div className="p-4 border-b flex items-center justify-between">
            <h4 className="font-semibold">Chat History</h4>
            <Button variant="outline" size="sm" onClick={startNewSession}>
              <Plus className="w-4 h-4 mr-2" />
              New Chat
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {sessions.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                No previous chats
              </div>
            ) : (
              sessions.map((session) => (
                <div 
                  key={session.id}
                  className={`p-3 rounded-lg cursor-pointer hover:bg-gray-100 flex items-center justify-between ${
                    currentSessionId === session.id ? 'bg-purple-50 border border-purple-200' : 'bg-gray-50'
                  }`}
                  onClick={() => loadSession(session.id)}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{session.title}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(session.updated_at).toLocaleDateString()} • {session.message_count} messages
                    </p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); deleteSession(session.id); }}
                    className="text-red-500 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))
            )}
          </div>
          <div className="p-4 border-t">
            <Button 
              variant="outline" 
              className="w-full" 
              onClick={() => setShowHistory(false)}
            >
              Back to Chat
            </Button>
          </div>
        </div>
      )}

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-4">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <h3 className="font-semibold text-lg text-gray-800 mb-2">
              How can I help you today?
            </h3>
            <p className="text-gray-500 text-sm mb-6 max-w-xs">
              I can answer safety questions, help with audits, and perform actions in the platform.
            </p>
            
            {/* Quick Actions */}
            <div className="grid grid-cols-2 gap-2 w-full max-w-xs mb-4">
              {QUICK_ACTIONS.map((action, idx) => (
                <Button
                  key={idx}
                  variant="outline"
                  size="sm"
                  className="justify-start text-xs"
                  onClick={() => sendMessage(action.text)}
                >
                  <action.icon className="w-3 h-3 mr-2" />
                  {action.text.split(' ').slice(0, 3).join(' ')}...
                </Button>
              ))}
            </div>

            {/* Suggestions */}
            <div className="space-y-2 w-full max-w-xs">
              {SUGGESTIONS.slice(0, 3).map((suggestion, idx) => (
                <button
                  key={idx}
                  className="w-full text-left px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
                  onClick={() => sendMessage(suggestion)}
                >
                  <ChevronRight className="w-4 h-4 text-purple-500" />
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {messages.map((message, idx) => (
              <ChatMessage 
                key={idx} 
                message={message} 
                isUser={message.role === 'user'} 
              />
            ))}
            {loading && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <div className="bg-gray-100 rounded-2xl rounded-tl-sm px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-purple-600" />
                    <span className="text-sm text-gray-500">Thinking...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input Area */}
      <div className="p-4 border-t bg-gray-50 rounded-b-2xl">
        <div className="flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask me anything about safety..."
            disabled={loading}
            className="flex-1"
          />
          <Button 
            onClick={() => sendMessage()} 
            disabled={!input.trim() || loading}
            className="bg-purple-600 hover:bg-purple-700"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Floating Chat Button Component
export const FloatingChatButton = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-full shadow-lg hover:shadow-xl transition-all flex items-center justify-center z-50 group"
        >
          <MessageSquare className="w-6 h-6 text-white" />
          <span className="absolute -top-2 -right-2 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
            <Sparkles className="w-3 h-3 text-white" />
          </span>
        </button>
      )}
      
      {isOpen && (
        <AIAssistant isFloating={true} onClose={() => setIsOpen(false)} />
      )}
    </>
  );
};

// Full Page AI Assistant Component
const AIAssistantPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-xl flex items-center justify-center">
              <Bot className="w-5 h-5 text-white" />
            </div>
            AI Safety Assistant
          </h1>
          <p className="text-gray-600 mt-1">
            Your intelligent OSHA safety companion - ask questions, get guidance, and perform actions
          </p>
        </div>
        
        <AIAssistant isFloating={false} />
      </div>
    </div>
  );
};

export default AIAssistantPage;
